/** What a coverage cell leads to: the path to the figure when data arrived, the gap - with its owner
 * and its effect on the total - when it did not (v5 stage F2, Coverage).
 *
 * Metadata, not paragraphs: three labelled facts, then the way on.
 *
 * **The effect line is EmitIQ's own note, labelled so, and it states no number.** What a missing
 * window "costs" in tonnes is not known - nothing arrived to price - and an estimate from last
 * year's same month would be a figure with no receipt (rule 3). So the line says what the *census
 * semantics* already say: a gap is absent from every total, not zero; a declared nil is counted as
 * a person's statement of nothing; a window not yet due is not counted either way. Those are the
 * six states' own meanings (`sourceModel.STATE`), restated as an effect, not a new claim.
 *
 * **The owner is read, never guessed.** `owner_user_id` is the census's; a name is shown only when
 * the roster could be read (an Admin's), otherwise it says *someone is named* - and never a name it
 * does not have. A source with nobody named says so, because that is a staffing decision the
 * onboarding sentence already counts.
 *
 * **A waiting document is a note and nothing else** (ADR-0166). `waiting` is the annotation
 * `GET /coverage` puts on a window; it is drawn by `WaitingNote` and read by nothing here, so the
 * facts above - owner, effect on the total - are the window's own state's, unchanged.
 *
 * **The path for a submitted window is the source's own trail** (`/sources/{id}/links`): the
 * readings in force and the documents behind them, each a link. A failed read says so with a retry
 * and never an empty list that would read as "nothing arrived".
 */

import { Link } from "@tanstack/react-router";

import { useMe } from "../../api/auth";
import type { WaitingDocument } from "../../api/coverageRollup";
import type { SourceCoverage, SourceWindow } from "../../api/sources";
import { useSourceLinks } from "../../api/sources";
import { ReadFailed } from "../../components/Loaded";
import { isTabularContentType } from "../../lib/documents";
import { shortDate, statedQuantity, unitLabel } from "../../lib/format";
import { A_COLLEAGUE, useNameOf } from "../PersonRef";
import { keepSearch } from "../search";
import { EFFECT } from "./effect";
import { WaitingNote } from "./WaitingNote";
import styles from "./CellPath.module.css";

/** The roles that may put a reading in (`evidence.py` MAY_UPLOAD): the only ones offered the route. */
const MAY_UPLOAD: readonly string[] = ["admin", "approver", "contributor"];

export function CellPath({
  source,
  window,
  waiting = [],
  holdDays,
}: {
  source: SourceCoverage;
  window: SourceWindow;
  /** Documents that may cover this window and are waiting for review (`GET /coverage`,
   * ADR-0166). A note beside the state, never a change to it: nothing below reads this to decide
   * what the window is. */
  waiting?: readonly WaitingDocument[];
  /** Days the gap reminder is held for a window with a waiting document (`GET /coverage`,
   * ADR-0206). Said beside the note; changes nothing about the window. */
  holdDays?: number;
}) {
  const { data: me } = useMe();
  const personName = useNameOf();
  const owner = source.owner_user_id;
  const ownerWord =
    owner === null ? null : owner === me?.user_id ? "You" : (personName(owner) ?? A_COLLEAGUE);
  const effect = EFFECT[window.state];
  const mayUpload = me ? MAY_UPLOAD.includes(me.role) : false;

  return (
    <div className={styles.path} data-testid="cell-path" data-state={window.state}>
      <dl className={styles.facts}>
        <div>
          <dt>Owner</dt>
          <dd data-testid="cell-owner" data-owned={owner === null ? "no" : "yes"}>
            {ownerWord ?? <span className={styles.warn}>Nobody named</span>}
          </dd>
        </div>
        <div>
          <dt>Effect on the total</dt>
          <dd data-testid="cell-effect" data-tone={effect?.tone ?? "neutral"}>
            {effect?.word ?? "Not stated"}
          </dd>
        </div>
      </dl>
      <p className={styles.label}>EmitIQ&rsquo;s note, not a disclosure and not a figure.</p>

      <WaitingNote documents={waiting} state={window.state} cadence={source.cadence} holdDays={holdDays} />

      {window.state === "submitted" ? (
        <Trail source={source} window={window} />
      ) : null}

      {window.state === "missing" && mayUpload ? (
        <p className={styles.next}>
          <Link className={styles.link} to="/evidence" search={keepSearch} data-testid="cell-upload">
            Upload the bill for this period
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function Trail({ source, window }: { source: SourceCoverage; window: SourceWindow }) {
  const links = useSourceLinks(source.source_id, window.period_start, window.period_end);
  if (links.isPending) return <p className={styles.label}>Reading what arrived</p>;
  if (links.error) {
    return (
      <ReadFailed
        of="The path to this figure"
        error={links.error}
        onRetry={() => void links.refetch()}
      />
    );
  }
  const data = links.data;
  if (!data || (data.activities.length === 0 && data.documents.length === 0)) {
    return <p className={styles.label}>Nothing is recorded against this period.</p>;
  }
  return (
    <div data-testid="cell-trail">
      <p className={styles.head}>Path to the figure</p>
      <ul className={styles.list}>
        {data.activities.map((activity) => (
          <li key={activity.activity_id}>
            <Link
              className={styles.link}
              to="/records/$recordId"
              params={{ recordId: activity.activity_id }}
              search={keepSearch}
            >
              {shortDate(activity.period_start)} – {shortDate(activity.period_end)}
            </Link>
            <span className={styles.meta}>
              {statedQuantity(activity.quantity)} {unitLabel(activity.unit)}
              {activity.is_effective ? "" : " · superseded"}
            </span>
          </li>
        ))}
        {data.documents.map((document) => (
          <li key={document.document_id}>
            <Link
              className={styles.link}
              to={isTabularContentType(document.content_type) ? "/evidence/$documentId/import" : "/evidence/$documentId"}
              params={{ documentId: document.document_id }}
              search={keepSearch}
            >
              {document.original_filename ?? "Document"}
            </Link>
            <span className={styles.meta}>{document.state.replace(/_/g, " ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
